import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { FolderTree } from "lucide-react";

/** Rename or merge product categories so each kind of goods lives in exactly one category. */
export const CategoryManager = ({ kind, groups, onDone }) => {
  const { t } = useI18n();
  const [target, setTarget] = useState({});
  const [busy, setBusy] = useState("");
  const names = groups.map(([c]) => c);

  const apply = async (old) => {
    const next = (target[old] || "").trim();
    if (!next || next === old) return;
    setBusy(old);
    try {
      const { data } = await api.put("/products/categories/rename", { kind, old, new: next });
      if (data.moved === 0) toast.info(t("no_data"));
      else toast.success(`${t("merged_ok")}: ${data.category}`);
      setTarget({ ...target, [old]: "" });
      onDone();
    } catch (e) {
      toast.error(errMsg(e, "Gagal menggabungkan kategori"));
    } finally {
      setBusy("");
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3"
      data-testid="category-manager"
    >
      <p className="font-display font-semibold flex items-center gap-2">
        <FolderTree size={18} className="text-primary" /> {t("manage_cats")}
      </p>
      <p className="text-xs text-muted-foreground">{t("manage_cats_hint")}</p>
      <Button
        size="sm"
        variant="outline"
        data-testid="dedupe-cats-btn"
        onClick={async () => {
          setBusy("__dedupe__");
          try {
            const { data } = await api.post("/products/categories/dedupe");
            toast.success(`${t("tidy_cats")} · ${data.merged.length}`);
            onDone();
          } catch (e) {
            toast.error(errMsg(e, "Gagal merapikan kategori"));
          } finally {
            setBusy("");
          }
        }}
        disabled={busy === "__dedupe__"}
      >
        {t("tidy_cats")}
      </Button>
      {groups.map(([cat, rows]) => (
        <div key={cat} className="flex flex-wrap items-center gap-2" data-testid={`cat-manage-${cat}`}>
          <span className="text-sm font-medium min-w-[140px]">
            {cat} <span className="text-muted-foreground">· {rows.length}</span>
          </span>
          <Input
            data-testid={`cat-target-${cat}`}
            className="max-w-xs"
            placeholder={t("merge_to")}
            list={`cat-options-${kind}`}
            value={target[cat] || ""}
            onChange={(e) => setTarget({ ...target, [cat]: e.target.value })}
          />
          <Button
            size="sm"
            variant="outline"
            data-testid={`cat-apply-${cat}`}
            disabled={busy === cat || !(target[cat] || "").trim()}
            onClick={() => apply(cat)}
          >
            {t("merge_btn")}
          </Button>
        </div>
      ))}
      <datalist id={`cat-options-${kind}`}>
        {names.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
    </div>
  );
};
